'use client';

import { redirect, usePathname } from 'next/navigation';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/dashboard/Sidebar';
import { DashboardShellProvider } from '@/components/dashboard/dashboard-shell-context';

/** The COP. The only route whose sidebar floats over the page. */
const COP_ROUTE = '/';

/** What the dashboard shows before it knows who is looking at it. */
function DashboardInitializing() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="text-center">
        <div className="mb-3 size-8 mx-auto animate-spin rounded-full border-2 border-border border-t-primary" />
        <p className="text-sm text-muted-foreground">Initializing…</p>
      </div>
    </div>
  );
}

function DashboardShell({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const pathname = usePathname();

  /**
   * The rail FLOATS on the COP, and nowhere else.
   *
   * It used to be frozen there, because an animating sidebar width in the map's
   * own flex row is a `map.resize()` on every animation frame
   * (`DroneMap.tsx:781`) - the aircraft under the operator's cursor sliding for
   * the length of the transition. Taking the rail out of the row removes the
   * cause rather than the symptom, so the expansion comes back.
   *
   * Only here. Every other route keeps the rail in flow: there is no map, a
   * width change costs nothing, and a route-specific overlay everywhere would be
   * a second layout to maintain for no gain (D8).
   */
  const railFloats = pathname === COP_ROUTE;

  if (!loading && !user) {
    redirect('/login');
  }

  if (loading) return <DashboardInitializing />;

  if (!user) return null;

  return (
    <DashboardShellProvider>
      {/* `relative` is what the floating rail is positioned against. Harmless on
          every other route, where the rail is still a flex child and this box
          establishes nothing anyone uses. */}
      <div className="relative flex h-screen min-w-0 bg-background">
        <Sidebar floating={railFloats} />
        {/* Deliberately NOT offset by the rail. The map wants to start at x=0 and
            run continuously under it; the surfaces that must not hide beneath it
            inset themselves, off the width the rail publishes. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{children}</main>
      </div>
    </DashboardShellProvider>
  );
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <DashboardShell>{children}</DashboardShell>
    </AuthProvider>
  );
}
